'use client';

import { ReactNode } from 'react';

import { useTranslations } from '../../../i18n/TranslationsProvider';
import { ImageBlock } from '../../../types/schemas';
import { cn } from '../../../utils/css/cn';
import { getFallbackImgUrl } from '../../../utils/video/getFallbackImgUrl';

type Props = {
	fallbackImg?: ImageBlock;
	showFallbackImage?: boolean;
	playVideo?: () => void;
	children?: ReactNode;
	className?: string;
};

export const YouTubePlayerFallbackImg = ({
	className,
	children,
	fallbackImg,
	showFallbackImage,
	playVideo,
}: Props) => {
	const { t } = useTranslations();

	const fallbackImageUrl = getFallbackImgUrl(fallbackImg?.url);

	if (!fallbackImageUrl) return <>{children}</>;

	const isInteractive = !!playVideo;

	return (
		<div className={className}>
			<button
				onClick={playVideo}
				type="button"
				style={{ backgroundImage: `url(${fallbackImageUrl})` }}
				className={cn(
					'group',
					'relative',
					'w-full h-full',
					'bg-black bg-position-[50%] bg-cover',
					isInteractive
						? 'cursor-pointer focus:border focus:border-takeda-dark-red-600'
						: 'cursor-auto',
				)}
				disabled={!isInteractive}
				aria-label={t.general.video.playVideo}
			>
				<div
					className={cn(
						'absolute top-[50%] left-[50%]',
						'-translate-x-[50%] -translate-y-[50%]',
						'w-[70px] h-[46px]',
						'bg-takeda-dark-grey-900 group-hover:bg-takeda-dark-red-600 group-focus:bg-takeda-dark-red-600',
						'opacity-80 group-hover:opacity-100 group-focus:opacity-100',
						'rounded-[14%]',
						'transition-all',
						'before:absolute before:top-[50%] before:left-[50%]',
						'before:-translate-x-[50%] before:-translate-y-[50%]',
						'before:border-[11px_0_11px_19px] before:border-[transparent_transparent_transparent_#fff]',
						(!showFallbackImage || !isInteractive) && 'hidden',
					)}
				></div>
				<div className={cn('w-full h-full', showFallbackImage && 'hidden')}>{children}</div>
			</button>
		</div>
	);
};

YouTubePlayerFallbackImg.displayName = 'YouTubePlayerFallbackImg';

export default YouTubePlayerFallbackImg;
